import React, { useState } from 'react';
import { Star, Heart, Shield, Award, Users, Clock, MapPin, CheckCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getDisplayName } from '../utils/displayHelpers';
import { getServiceLabel } from '../config/services';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const CaregiverCard = ({ caregiver }) => {
  const [imageError, setImageError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const { user, isFavorite, toggleFavorite } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const favorited = isFavorite(caregiver.id);

  const handleToggleFavorite = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }

    setSavingFavorite(true);
    try {
      const nowFavorited = await toggleFavorite(caregiver.id);
      toast?.success?.(nowFavorited ? 'Saved to your favorites' : 'Removed from favorites');
    } catch (err) {
      toast?.error?.('Could not update favorites. Please try again.');
    } finally {
      setSavingFavorite(false);
    }
  };

  const getBadgeConfig = (badge) => {
    const badgeConfigs = {
      verified: { text: 'Active', icon: <CheckCircle className="w-3 h-3" /> },
      vetChecked: { text: 'Pet First Aid', icon: <Shield className="w-3 h-3" /> },
      superhost: { text: 'Top Rated', icon: <Award className="w-3 h-3" /> },
      premium: { text: 'Premium', icon: <Star className="w-3 h-3" /> },
      trainer: { text: 'Trainer', icon: <Users className="w-3 h-3" /> },
      catSpecialist: { text: 'Cat Expert', icon: <Heart className="w-3 h-3" /> },
      daycare: { text: 'Daycare', icon: <Users className="w-3 h-3" /> },
      socializing: { text: 'Social', icon: <Users className="w-3 h-3" /> },
      luxury: { text: 'Luxury', icon: <Award className="w-3 h-3" /> },
      grooming: { text: 'Grooming', icon: <Star className="w-3 h-3" /> },
      walker: { text: 'Walker', icon: <MapPin className="w-3 h-3" /> },
      'gps-tracking': { text: 'GPS Track', icon: <MapPin className="w-3 h-3" /> },
      boarding: { text: 'Boarding', icon: <Shield className="w-3 h-3" /> },
      adventure: { text: 'Adventure', icon: <MapPin className="w-3 h-3" /> },
      outdoor: { text: 'Outdoor', icon: <MapPin className="w-3 h-3" /> },
      medical: { text: 'Medical', icon: <Shield className="w-3 h-3" /> },
      'senior-care': { text: 'Senior Care', icon: <Heart className="w-3 h-3" /> },
      'puppy-specialist': { text: 'Puppy Expert', icon: <Heart className="w-3 h-3" /> },
      training: { text: 'Training', icon: <Users className="w-3 h-3" /> },
      'exotic-specialist': { text: 'Exotic Expert', icon: <Star className="w-3 h-3" /> },
      'habitat-expert': { text: 'Habitat', icon: <Shield className="w-3 h-3" /> },
    };

    return badgeConfigs[badge] || { text: badge, icon: <Star className="w-3 h-3" /> };
  };

  const fallbackImage = 'https://images.unsplash.com/photo-1587559045816-8b0a54d4a55f?w=800&h=600&fit=crop';
  const fallbackAvatar = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face';

  return (
    <article className="pf-caregiver-card group">
      <div className="pf-caregiver-card__media">
        <img
          src={imageError ? fallbackImage : (caregiver.homePhotos?.[0] || fallbackImage)}
          alt={`${getDisplayName(caregiver.user)} service preview`}
          className="pf-caregiver-card__image"
          onError={() => setImageError(true)}
          loading="lazy"
        />

        <button
          type="button"
          onClick={handleToggleFavorite}
          disabled={savingFavorite}
          aria-pressed={favorited}
          aria-label={favorited ? 'Remove from favorites' : 'Save to favorites'}
          className="pf-caregiver-card__favorite"
        >
          <Heart
            className={`w-4 h-4 ${favorited ? 'text-red-500 fill-red-500' : 'text-[var(--pf-ink-muted)]'}`}
          />
        </button>

        <div className="pf-caregiver-card__rating">
          <Star className="w-4 h-4 text-yellow-500 fill-current" />
          <span className="text-sm font-semibold text-[var(--pf-ink)]">{caregiver.rating}</span>
          <span className="text-xs text-[var(--pf-ink-muted)]">({caregiver.reviewCount})</span>
        </div>
      </div>

      <div className="pf-caregiver-card__body">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative shrink-0">
            <img
              src={avatarError ? fallbackAvatar : (caregiver.user.avatar || fallbackAvatar)}
              alt={getDisplayName(caregiver.user)}
              className="w-12 h-12 rounded-full object-cover border-2 border-[var(--pf-surface)] shadow-sm"
              onError={() => setAvatarError(true)}
            />
            {caregiver.isBackgroundChecked && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                <CheckCircle className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-[var(--pf-ink)] truncate text-lg">
              {getDisplayName(caregiver.user)}
            </h3>
            <p className="text-sm text-[var(--pf-ink-muted)] truncate">{caregiver.title}</p>
          </div>
        </div>

        <p className="text-sm text-[var(--pf-ink-muted)] mb-4 line-clamp-2 leading-relaxed">
          {caregiver.bio}
        </p>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs font-medium text-[var(--pf-ink-muted)] uppercase tracking-wide">Accepts:</span>
          {caregiver.speciesAccepted.map((species) => (
            <span key={species} className="text-xs px-2 py-1 bg-[var(--pf-mint)] text-[var(--pf-ink)] rounded-full">
              {species}
            </span>
          ))}
        </div>

        {Array.isArray(caregiver.serviceSlugs) && caregiver.serviceSlugs.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-[var(--pf-ink-muted)] mb-1.5">Services</div>
            <div className="flex flex-wrap gap-1.5">
              {caregiver.serviceSlugs.slice(0, 3).map((slug) => (
                <span
                  key={slug}
                  className="text-xs px-2 py-1 rounded-full bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] font-medium"
                >
                  {getServiceLabel(slug)}
                </span>
              ))}
              {caregiver.serviceSlugs.length > 3 && (
                <span className="text-xs px-2 py-1 rounded-full bg-[var(--pf-mint)] text-[var(--pf-ink-muted)]">
                  +{caregiver.serviceSlugs.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          {caregiver.badges.slice(0, 2).map((badge) => {
            const config = getBadgeConfig(badge);
            return (
              <span
                key={badge}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]"
              >
                {config.icon}
                {config.text}
              </span>
            );
          })}
          {caregiver.badges.length > 2 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--pf-mint)] text-[var(--pf-ink-muted)]">
              +{caregiver.badges.length - 2} more
            </span>
          )}
        </div>

        <div className="flex items-center justify-between mb-4 text-xs text-[var(--pf-ink-muted)]">
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            <span>Up to {caregiver.maxPets} pets</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>Available today</span>
          </div>
        </div>

        <div className="pt-4 border-t border-[var(--pf-border)]">
          <div className="mb-3">
            <div className="text-xs text-[var(--pf-ink-muted)] mb-1">Starting from</div>
            <div className="text-xl font-bold text-[var(--pf-ink)]">
              ₹{caregiver.hourlyRate}
              <span className="text-sm font-normal text-[var(--pf-ink-muted)]">/hr</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/profile/${caregiver.id}`}
              className="pf-btn-secondary text-center text-sm py-2.5"
            >
              View profile
            </Link>
            <Link
              to={`/owner-booking/${caregiver.id}`}
              className="pf-btn-primary text-center text-sm py-2.5"
            >
              Book now
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

export default CaregiverCard;
